import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { TextareaModule } from 'primeng/textarea';
import { MentionDirective } from '../../../@core/directives/mention.directive';
import { TextareaSizeStorageService } from '../../../shared/services/textarea-size-storage.service';

@Component({
  selector: 'app-formly-phx-textarea',
  templateUrl: './formly-phx-textarea.component.html',
  styleUrl: './formly-phx-textarea.component.scss',
  standalone: true,
  host: {
    class: 'block w-full min-h-0',
    '[class.phx-formly-textarea--fill]': '!!props?.fillHeight',
  },
  imports: [
    TextareaModule,
    FormlyModule,
    ReactiveFormsModule,
    MentionDirective
  ]
})
export class FormlyPhxTextareaComponent extends FieldType implements AfterViewInit, OnDestroy {
  @ViewChild('textareaRef') textareaRef?: ElementRef<HTMLTextAreaElement>;

  private readonly textareaSizeStorage = inject(TextareaSizeStorageService);
  private startHeight = 0;
  private activeTextarea: HTMLTextAreaElement | null = null;
  private activeStorageKey: string | null = null;
  private persistenceListenersAttached = false;

  private readonly onTextareaPointerDown = (): void => {
    const textarea = this.textareaRef?.nativeElement;
    const storageKey = this.storageKey;
    if (!textarea || !storageKey) {
      return;
    }
    this.activeTextarea = textarea;
    this.activeStorageKey = storageKey;
    this.startHeight = Math.round(textarea.getBoundingClientRect().height);
  };

  private readonly onDocumentPointerUp = (): void => {
    if (!this.activeTextarea || !this.activeStorageKey) {
      return;
    }

    const textarea = this.activeTextarea;
    const storageKey = this.activeStorageKey;
    this.activeTextarea = null;
    this.activeStorageKey = null;

    if (!document.body.contains(textarea)) {
      return;
    }

    const endHeight = Math.round(textarea.getBoundingClientRect().height);
    // 2px threshold: getBoundingClientRect() returns sub-pixel values; after Math.round(),
    // two reads of the same untouched element can differ by 1px due to browser zoom,
    // HiDPI scaling, or CSS sub-pixel rendering. 2px filters rounding noise.
    if (endHeight > 0 && Math.abs(endHeight - this.startHeight) > 2) {
      this.textareaSizeStorage.saveSize(storageKey, endHeight);
    }
  };

  ngAfterViewInit(): void {
    if (!this.props?.persistSize) {
      return;
    }

    const storageKey = this.storageKey;
    const textarea = this.textareaRef?.nativeElement;
    if (!storageKey || !textarea) {
      return;
    }

    this.loadAndApplySavedHeight(textarea, storageKey);
    this.attachPointerResizePersistence(textarea);
  }

  ngOnDestroy(): void {
    const textarea = this.textareaRef?.nativeElement;
    if (textarea && this.persistenceListenersAttached) {
      textarea.removeEventListener('pointerdown', this.onTextareaPointerDown);
      document.removeEventListener('pointerup', this.onDocumentPointerUp);
      this.persistenceListenersAttached = false;
    }
    this.activeTextarea = null;
    this.activeStorageKey = null;
  }

  get storageKey(): string | null {
    if (!this.props?.persistSize) {
      return null;
    }
    if (this.props?.sizeStorageKey) {
      return this.props.sizeStorageKey as string;
    }
    const detailName = this.options?.formState?.detailComponent?.constructor?.name;
    return detailName ? `${detailName}:${this.field.key}` : null;
  }

  private loadAndApplySavedHeight(textarea: HTMLTextAreaElement, storageKey: string): void {
    const height = this.textareaSizeStorage.getClampedHeight(storageKey);
    if (height) {
      textarea.style.setProperty('height', `${height}px`, 'important');
    }
  }

  private attachPointerResizePersistence(textarea: HTMLTextAreaElement): void {
    if (this.persistenceListenersAttached) {
      return;
    }
    textarea.addEventListener('pointerdown', this.onTextareaPointerDown);
    document.addEventListener('pointerup', this.onDocumentPointerUp);
    this.persistenceListenersAttached = true;
  }
}
